import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/types/news";
import { formatViews, getCategoryName } from "@/utils/articles";

interface HeroCarouselProps {
  articles: Article[];
}

export default function HeroCarousel({ articles }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = articles.length;

  useEffect(() => {
    if (total <= 1 || paused) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [total, paused]);

  useEffect(() => {
    if (index > total - 1) setIndex(0);
  }, [index, total]);

  if (total === 0) {
    return (
      <div className="h-[320px] md:h-[420px] rounded-lg border border-dashed border-background-300 bg-background-100 flex flex-col items-center justify-center text-center px-6">
        <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 mb-3">
          <i className="ri-inbox-line text-2xl" aria-hidden="true" />
        </span>
        <p className="font-heading text-lg font-bold text-foreground-800">暂无精选爆料</p>
        <p className="mt-1 text-sm text-foreground-500">编辑部正在核实新的线索，稍后再来看看。</p>
        <Link
          to="/search"
          className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-primary-600 text-background-50 text-sm hover:bg-primary-700 transition-colors"
        >
          浏览全部爆料
        </Link>
      </div>
    );
  }

  const active = articles[Math.min(index, total - 1)];

  return (
    <section
      className="relative rounded-lg overflow-hidden bg-secondary-900 group"
      aria-label="焦点爆料轮播"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[330px] sm:h-[390px] lg:h-[446px] w-full">
        {articles.map((article, itemIndex) => (
          <Link
            key={article.id}
            to={`/detail/${article.id}`}
            className={`absolute inset-0 cursor-pointer transition-opacity duration-700 ${
              itemIndex === index ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
            }`}
            aria-hidden={itemIndex !== index}
            tabIndex={itemIndex === index ? 0 : -1}
          >
            <img
              src={article.image}
              alt={`${article.title}配图`}
              title={`${article.title} - 51黑料网`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/45 to-foreground-950/10" />
            <div className="absolute inset-x-0 bottom-0 p-4 md:p-7">
              <div className="flex items-center gap-2 mb-2.5 flex-wrap">
                <span className="px-2 py-0.5 rounded-md bg-primary-600 text-background-50 text-[11px] font-medium">
                  {getCategoryName(article.category)}
                </span>
                <span className="px-2 py-0.5 rounded-md bg-background-50/15 border border-background-50/25 text-background-50 text-[11px]">
                  精选爆料
                </span>
                <span className="text-[11px] font-label text-background-50/70">{article.region}</span>
              </div>
              <h2 className="font-heading text-xl sm:text-2xl md:text-[30px] font-black leading-tight text-background-50 line-clamp-2">
                {article.title}
              </h2>
              <p className="hidden sm:block mt-2.5 text-sm leading-relaxed text-background-50/75 line-clamp-2 max-w-2xl">
                {article.summary}
              </p>
              <div className="mt-3 flex items-center gap-3 text-[11px] md:text-xs text-background-50/70 flex-wrap">
                <span>{article.author}</span>
                <span className="w-px h-3 bg-background-50/30" />
                <span className="font-label">{article.publishedAt.slice(5, 16).replace("T", " ")}</span>
                <span className="w-px h-3 bg-background-50/30" />
                <span>{formatViews(article.views)} 阅读</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="absolute top-3.5 right-3.5 md:top-4 md:right-4 z-20 flex items-center gap-2">
        <div className="flex items-center gap-1 mr-1">
          {articles.map((article, dotIndex) => (
            <button
              key={article.id}
              type="button"
              aria-label={`切换到第 ${dotIndex + 1} 条爆料`}
              onClick={() => setIndex(dotIndex)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                dotIndex === index ? "w-5 bg-accent-400" : "w-1.5 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          aria-label="上一条爆料"
          onClick={() => setIndex((prev) => (prev - 1 + total) % total)}
          className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-foreground-950/35 border border-background-50/25 text-background-50 backdrop-blur hover:bg-foreground-950/55 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-base" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="下一条爆料"
          onClick={() => setIndex((prev) => (prev + 1) % total)}
          className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-foreground-950/35 border border-background-50/25 text-background-50 backdrop-blur hover:bg-foreground-950/55 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
        </button>
      </div>

      <span className="sr-only">
        当前为第 {index + 1} 条，共 {total} 条：{active.title}
      </span>
    </section>
  );
}